import React from 'react';
import { FileSpreadsheet, FileText, FileType, File } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useRelativeTime } from '@hooks/useRelativeTime';
import type { DocumentSummary } from '../../types/dashboard';
import { cn } from '@/lib/utils';

const FILE_ICON: Record<string, typeof File> = {
  pdf: FileType,
  docx: FileText,
  xlsx: FileSpreadsheet,
};

const formatSize = (bytes: number) => {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

interface DocumentRowProps {
  document: DocumentSummary;
  onSelect?: (id: string) => void;
}

const DocumentRow: React.FC<DocumentRowProps> = ({ document: doc, onSelect }) => {
  const relativeTime = useRelativeTime();
  const Icon = FILE_ICON[doc.fileType] ?? File;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(doc.id)}
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left',
        'transition-colors duration-150 ease-shell hover:bg-accent/60',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-lg border bg-muted/40 text-muted-foreground transition-colors group-hover:text-foreground">
        <Icon className="size-[18px]" strokeWidth={1.75} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{doc.title}</span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {doc.uploadedBy} · {relativeTime(doc.uploadedAt)}
        </span>
      </span>

      <span className="hidden shrink-0 items-center gap-2 sm:flex">
        <Badge variant="secondary" className="font-normal">
          {doc.categoryName || doc.category}
        </Badge>
        <span className="w-16 text-right text-xs revy-figure text-muted-foreground">
          {formatSize(doc.size)}
        </span>
      </span>
    </button>
  );
};

export default DocumentRow;
